import { NumberInput, Select, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { ContextModalProps } from '@mantine/modals'
import Form from 'common/Form'
import NumberWithUnitInput from 'common/inputs/NumberWithUnitInput'
import { zodResolver } from 'mantine-form-zod-resolver'
import { useEffect } from 'react'
import { NewOrderItem } from 'types/orders'
import { Category, Unit } from 'types/products'
import { orderItemSchema } from 'types/schemas'
import { categoriesForSelect } from 'utils'

type Props = {
  initialValues?: NewOrderItem
  onSubmit: (values: NewOrderItem) => void
}

export default function OrderItemModal({
  context,
  id,
  innerProps: { initialValues, onSubmit },
}: ContextModalProps<Props>) {
  const form = useForm<NewOrderItem>({
    validate: zodResolver(orderItemSchema),
    initialValues: initialValues,
  })

  useEffect(() => {
    if (!initialValues) {
      form.initialize({ unit: Unit.PIECE } as NewOrderItem)
    }
  }, [initialValues, form])

  useEffect(() => {
    form.setFieldValue('unit', form.values.category === Category.FABRIC ? Unit.METER : Unit.PIECE)
  }, [form.getValues().category])

  return (
    <Form
      onSubmit={onSubmit}
      form={form}
      onCancel={() => context.closeModal(id)}
      submitText={initialValues ? 'Edytuj produkt' : 'Dodaj produkt'}
    >
      <Stack>
        <TextInput
          label="Produkt"
          description="Symbol produktu lub jego nazwa"
          placeholder="2705"
          withAsterisk
          {...form.getInputProps('productSymbol')}
        />
        <Select
          label="Rodzaj produktu"
          placeholder="Wybierz rodzaj produktu"
          data={categoriesForSelect}
          withAsterisk
          {...form.getInputProps('category')}
        />
        <TextInput
          label="Wymiar"
          description={`Wymiar brakującego produktu. ${form.getValues().category === Category.FABRIC ? 'Wysokość lub szerokość' : 'Wymiary'} w cm.`}
          placeholder={form.getValues().category === Category.FABRIC ? '250 cm' : '500x250 cm'}
          withAsterisk
          {...form.getInputProps('size')}
          onChange={(e) => form.setFieldValue('size', e.currentTarget.value.toLowerCase().replace(/\s+/g, ''))}
        />
        <NumberInput
          label="Cena"
          description="Cena jednej sztuki lub jednego metra produktu"
          placeholder="100 zł"
          suffix=" zł"
          allowNegative={false}
          decimalScale={2}
          fixedDecimalScale
          decimalSeparator=","
          hideControls
          withAsterisk
          {...form.getInputProps('price')}
        />
        <NumberWithUnitInput
          label="Ilość"
          description="Zamawiana ilość produktu"
          form={form}
          availableUnits={form.values.category === Category.FABRIC ? [Unit.METER] : [Unit.PIECE]}
          withAsterisk
        />
        <TextInput
          label="Wykończenie"
          description="Np. informacja o rodzaju taśmy lub przelotek"
          placeholder="Przelotki satyna"
          {...form.getInputProps('finish')}
        />
        <TextInput
          label="Uwagi"
          description="Dodatkowe informacje dotyczące produktu"
          placeholder="Dodatkowe informacje"
          maxLength={65}
          rightSection={<LetterCount value={form.values.comments} />}
          rightSectionWidth={35}
          {...form.getInputProps('comments')}
        />
      </Stack>
    </Form>
  )
}

function LetterCount({ value }: { value?: string | null }) {
  const length = value?.length || 0
  const color = length >= 60 ? 'red' : length >= 55 ? 'orange' : 'gray'
  return (
    <Text c={color} size="xs">
      {65 - length}
    </Text>
  )
}
